import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '@ardoq/i18n';
import { ErrorState, TimelineProperties, ViewLoadingStatus } from './types';
import { useResizeObserver } from '@ardoq/hooks';
import styled from 'styled-components';
import { TimelineViewContainer } from './styles';
import { TimelineZoomContainer as TimelineZoomContainerBase } from './chart/TimelineZoomContainer';
import { ChartWindowArgs } from '@ardoq/graph';
import { ArdoqLoaderSpinner, LoaderColor, LoaderSize } from '@ardoq/loader';
import { Centered } from '@ardoq/layout';
import { dispatchAction } from '@ardoq/rxbeach';
import { ErrorInfoBox } from '@ardoq/error-info-box';
import { viewReadyForExport } from '@ardoq/export/state';

const TimelineContentContainer = styled.div`
  flex: 1;
  position: relative;
  z-index: 0;
  overflow: hidden;
  &.forExport {
    height: fit-content;
    overflow: visible;
  }
`;
type UpdateRectFromLayoutEffectArgs = { height?: number; width?: number };

export const Timeline = ({
  viewStatus,
  timelineViewModelData: {
    timelineViewFieldsRegistry: {
      timeSpanFields,
      milestoneFields,
      orderedSelectedMilestoneFields,
      orderedSelectedPhaseFields,
    },
    legendComponentTypes,
    hasGrouping,
  },
  viewModelSourceData: {
    contextMenuName,
    contextMenuDataAttributeName,
    viewInstanceId,
    viewInterface: {
      componentDoubleClickHandler,
      componentSingleClickHandler,
      timelineCallbacks: {
        openDateRangeViewSetting,
        openMilestonesViewSetting,
        isPresentationMode,
        getNeverShowAgain,
        setNeverShowAgain,
      },
      trackingCallbacks,
      features: viewInterfaceFeatures,
    },
    errors,
    hasClones,
    isViewEmpty,
    users,
    i18nLocale,
    errorInfoBoxLabels,
  },
  timelineViewRenderingData: {
    timelineChartRenderingData: {
      timelineChartProperties,
      timelineChartRangeConstraints: { scalingLimitsForAdjustedGrid },
    },
    renderedItemLabels,
    renderedItemPhases,
    itemTopsInVirtualWindow,
    backgroundScrollHeight,
    transitLinksById,
  },
  isRenderingForExport,
  viewSettings,
  commands,
  ViewLegendComponent,
  EmptyStateComponent,
}: TimelineProperties) => {
  const { chartWindow, clientRect, dimensions } = timelineChartProperties;

  const {
    isLegendActive,
    selectedMilestoneFields: viewSettingsSelectedMilestones,
    selectedPhaseFields: viewSettingsSelectedPhases,
  } = viewSettings;

  // #region handle container resizing

  const contentPaneRef = useRef<HTMLDivElement | null>(null);
  const handleViewContainerResize = useCallback(
    ({ height = 0, width = 0 }: UpdateRectFromLayoutEffectArgs) => {
      if (clientRect.width === width && clientRect.height === height) {
        return;
      }
      if (clientRect.width !== width) {
        // reset labels column width to default when resizing the container
        commands.resetLabelsColumnWidth();
      }
      commands.setClientRect({ width, height });
    },
    [clientRect.height, clientRect.width, commands]
  );

  const setWindow = useCallback(
    (chartWindow: ChartWindowArgs) =>
      commands.setChartWindow(chartWindow, viewInstanceId),
    [viewInstanceId, commands]
  );

  useResizeObserver(contentPaneRef, handleViewContainerResize);

  // #endregion

  const [errorState, setErrorState] = useState<ErrorState>({
    isNeverShowAgainSet: undefined,
    clearedErrors: false,
    clearedHasClones: false,
  });

  useEffect(() => {
    if (errorState.isNeverShowAgainSet !== undefined) {
      return;
    }

    const readNeverShowAgain = async () => {
      const isNeverShowAgainSet = await getNeverShowAgain();
      if (isNeverShowAgainSet !== errorState.isNeverShowAgainSet) {
        setErrorState(prevState => ({ ...prevState, isNeverShowAgainSet }));
      }
    };

    readNeverShowAgain();
  }, [errorState.isNeverShowAgainSet, getNeverShowAgain]);

  useEffect(() => {
    commands.setContentContainer(() => contentPaneRef.current);
  }, [commands]);

  const isRenderingForExportRef = useRef(isRenderingForExport);
  useEffect(() => {
    if (isRenderingForExportRef.current === isRenderingForExport) {
      return;
    }

    if (isRenderingForExport) {
      dispatchAction(viewReadyForExport(), viewInstanceId);
    }

    isRenderingForExportRef.current = isRenderingForExport;
  }, [isRenderingForExport, viewInstanceId]);

  const showChart =
    !isViewEmpty &&
    Boolean(dimensions.chart.width) &&
    !(clientRect.width === 0 || clientRect.height === 0);

  // Set to true if errorState.isNeverShowAgainSet === undefined
  // to avoid showing the error info box before the actual value is set
  const isNeverShowAgainSet = errorState.isNeverShowAgainSet ?? true;

  useEffect(() => {
    if (showChart) {
      trackingCallbacks?.onRendered?.();
    }
  }, [showChart, trackingCallbacks]);

  return (
    <TimelineViewContainer data-context-menu={contextMenuName}>
      <ErrorInfoBox
        errors={errorState.clearedErrors ? [] : errors}
        hasClones={
          !isNeverShowAgainSet && !errorState.clearedHasClones && hasClones
        }
        clearHasClones={() => {
          if (errorState.clearedHasClones) {
            return;
          }
          setErrorState({ ...errorState, clearedHasClones: true });
        }}
        clearErrors={() => {
          if (errorState.clearedErrors) {
            return;
          }
          setErrorState({ ...errorState, clearedErrors: true });
        }}
        isShowNeverAgainSet={isNeverShowAgainSet}
        isPresentationMode={isPresentationMode()}
        toggleNeverShowAgain={() => {
          const isNeverShowAgainSet = !errorState.isNeverShowAgainSet;
          setNeverShowAgain(isNeverShowAgainSet);
          setErrorState({ ...errorState, isNeverShowAgainSet });
        }}
        {...errorInfoBoxLabels}
      />
      <TimelineContentContainer
        ref={contentPaneRef}
        aria-busy={viewStatus === ViewLoadingStatus.LOADING}
        aria-label={t(i18nLocale, 'timeline2024:ariaLabel', 'Timeline view')}
      >
        {viewStatus === ViewLoadingStatus.LOADING ? (
          <Centered>
            <ArdoqLoaderSpinner
              color={LoaderColor.GRAY}
              size={LoaderSize.MEDIUM}
            />
          </Centered>
        ) : isViewEmpty && EmptyStateComponent ? (
          <EmptyStateComponent
            timeSpanFields={timeSpanFields}
            milestoneFields={milestoneFields}
            selectedMilestoneFields={viewSettingsSelectedMilestones}
            selectedPhaseFields={viewSettingsSelectedPhases}
            openDateRangeViewSetting={openDateRangeViewSetting}
            openMilestonesViewSetting={openMilestonesViewSetting}
          />
        ) : showChart ? (
          <>
            <TimelineZoomContainerBase
              // zoom container props
              {...chartWindow}
              setWindow={setWindow}
              windowScalingLimitsForGrid={scalingLimitsForAdjustedGrid}
              // chart props
              componentDoubleClickHandler={componentDoubleClickHandler}
              componentSingleClickHandler={componentSingleClickHandler}
              hasGrouping={hasGrouping}
              contextMenuDataAttribute={contextMenuDataAttributeName}
              renderedItemLabels={renderedItemLabels}
              renderedItemPhases={renderedItemPhases}
              renderedItemTops={itemTopsInVirtualWindow}
              backgroundScrollHeight={backgroundScrollHeight}
              transitLinksById={transitLinksById}
              timelineChartProperties={timelineChartProperties}
              viewInstanceId={viewInstanceId}
              commands={commands}
            />
            <ViewLegendComponent
              legendComponentTypes={legendComponentTypes}
              orderedSelectedMilestoneFields={orderedSelectedMilestoneFields}
              orderedSelectedPhaseFields={orderedSelectedPhaseFields}
              isLegendActive={isLegendActive}
              isNewConditionalFormatting={Boolean(
                viewInterfaceFeatures?.isNewConditionalFormatting
              )}
              users={users}
            />
          </>
        ) : null}
      </TimelineContentContainer>
    </TimelineViewContainer>
  );
};
